/**
 * Truncated text cell for stats table (SMS text performance).
 * Layout: templateId textPreview tags | TXT N (right-aligned)
 * - Template ID (black)
 * - First 20 chars + "..." (grey) when text in DB — created with us
 * - "TXT" badge (orange) when NOT in smswiser_country_text — right side, left of drop count
 * - N = drop count (right-aligned, By text grouping only)
 * Hover: ID, Name, Tags (StatsHoverPopover). For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import { useToast } from '@/providers/ToastProvider';

import {
  StatsHoverPopover,
  StatsTooltipSection,
  StatsTooltipTagsBlock,
} from '../../../StatsHoverPopover';

const TEXT_PREVIEW_LEN = 20;

function truncateStart(text: string, maxLen: number): string {
  const t = text.trim();
  if (!t) return '—';
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}...`;
}

interface TruncatedTextCellProps {
  label: string;
  textId?: string | null;
  /** Full SMS text for tooltip (from campaign library when in DB; else label/Voluum value) */
  fullText?: string | null;
  /** true = text is in our DB (country creative); false = not in DB (e.g. sent manually to SMSWiser) */
  inDb?: boolean;
  /** Number of drops using this template (date-range filtered) */
  dropCount?: number;
  /** Show drop count next to TXT only when true (By text grouping) */
  showDropCount?: boolean;
  /** Performance tags from text-performance-tags API */
  performanceTags?: string[];
  /** Tag + date range for hover tooltip */
  performanceTagDetails?: Array<{ tag: string; dateRangePreset: string }>;
  /** Custom colors for tags (rule name -> hex) */
  tagColors?: Record<string, string>;
}

export const TruncatedTextCell: FC<TruncatedTextCellProps> = ({
  label,
  textId,
  fullText,
  inDb = true,
  dropCount,
  showDropCount = false,
  performanceTags,
  performanceTagDetails,
  tagColors,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const displayText = fullText ?? label ?? textId ?? '—';

  const copyToClipboard = (text: string, copyLabel: string) => {
    if (!text || text === '—') return;
    navigator.clipboard
      .writeText(text)
      .then(() => toastSuccess(`${copyLabel} copied`, 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const handleCopyTemplateId = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    copyToClipboard(textId ?? '', 'Template ID');
  };

  const handleCopyText = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    copyToClipboard(displayText, 'SMS text');
  };

  const textPreview =
    inDb &&
    displayText &&
    displayText !== '—' &&
    displayText !== textId
      ? truncateStart(displayText, TEXT_PREVIEW_LEN)
      : null;

  const externalTxtBadge =
    !inDb && textId ? (
      <span
        className="shrink-0 rounded px-1 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
        title="Not in campaign library — created outside our app"
      >
        TXT
      </span>
    ) : null;

  const showCount = showDropCount && dropCount != null && dropCount >= 1;
  const nValue = showCount ? dropCount : null;

  const tagsLine = (performanceTags?.length ?? 0) > 0 ? (
    <LeadsListTags
      performanceTags={performanceTags ?? []}
      tagColors={tagColors}
      className="inline-flex ml-1"
      compact
      disableTooltip
    />
  ) : null;

  const hoverContent = (
    <div className="flex flex-col gap-1.5 text-left">
      <StatsTooltipSection label="ID" valueClassName="font-mono">
        {textId || '—'}
      </StatsTooltipSection>
      <StatsTooltipSection label="Name" valueClassName="font-medium">
        {displayText || '—'}
      </StatsTooltipSection>
      {inDb ? (
        <div className="text-[10px] opacity-80">In campaign library</div>
      ) : (
        <div className="text-[10px] opacity-80">
          Not in campaign library — scale/copy to add
        </div>
      )}
      {((performanceTagDetails?.length ?? performanceTags?.length) ?? 0) > 0 && (
        <div>
          <StatsTooltipTagsBlock
            performanceTags={performanceTags}
            performanceTagDetails={performanceTagDetails}
            tagColors={tagColors}
          />
        </div>
      )}
    </div>
  );

  const templateIdButton = (
    <button
      type="button"
      onClick={handleCopyTemplateId}
      title="Click to copy template ID"
      className="shrink-0 cursor-pointer rounded px-0.5 -mx-0.5 text-foreground hover:bg-muted/60"
      aria-label="Copy template ID"
    >
      {textId ?? '—'}
    </button>
  );

  return (
    <StatsHoverPopover content={hoverContent}>
      <div className="flex w-full min-w-0 items-baseline justify-between gap-2 truncate text-left text-xs font-medium cursor-default">
        <span className="flex min-w-0 shrink items-baseline gap-1.5 truncate">
          {templateIdButton}
          {textPreview ? (
            <button
              type="button"
              onClick={handleCopyText}
              title="Click to copy SMS text"
              className="min-w-0 truncate cursor-pointer rounded px-0.5 -mx-0.5 text-left text-muted-foreground hover:bg-muted/60"
              aria-label="Copy SMS text"
            >
              {textPreview}
            </button>
          ) : null}
          {tagsLine}
        </span>
        {(externalTxtBadge || nValue != null) ? (
          <span className="shrink-0 flex items-baseline gap-1.5">
            {externalTxtBadge}
            {nValue != null ? (
              <span className="font-semibold tabular-nums text-foreground">
                {nValue}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    </StatsHoverPopover>
  );
};
